import { cn } from '@/lib/utils';
import { PASSWORD_REQUIREMENTS, type PasswordAssessment } from '@/lib/auth/passwords';

interface PasswordStrengthMeterProps {
  readonly assessment: PasswordAssessment;
}

const LABEL_TEXT: Record<PasswordAssessment['label'], string> = {
  empty: '',
  'too-weak': 'Too weak',
  weak: 'Weak',
  fair: 'Almost there',
  strong: 'Strong',
};

const LABEL_TONE: Record<PasswordAssessment['label'], string> = {
  empty: '',
  'too-weak': 'text-destructive',
  weak: 'text-destructive',
  fair: 'text-warning',
  strong: 'text-success',
};

function barTone(score: number, segmentIndex: number): string {
  if (segmentIndex >= score) return 'bg-border';
  if (score <= 2) return 'bg-destructive';
  if (score === 3) return 'bg-warning';
  return 'bg-success';
}

export function PasswordStrengthMeter({ assessment }: PasswordStrengthMeterProps) {
  const { score, label, checks, meetsRequirements } = assessment;

  // Build a one-line "what to fix" hint for the field. Empty input shows the
  // baseline requirement; partial input names the first missing rule so the
  // UI doesn't dump the whole list under every keystroke.
  const hint = (() => {
    if (label === 'empty') {
      return `At least ${String(PASSWORD_REQUIREMENTS.minLength)} characters with upper, lower, number, and symbol.`;
    }
    if (meetsRequirements) {
      return 'Looks good.';
    }
    if (!checks.minLength) {
      return `Use at least ${String(PASSWORD_REQUIREMENTS.minLength)} characters.`;
    }
    if (!checks.lowercase) return 'Add a lowercase letter.';
    if (!checks.uppercase) return 'Add an uppercase letter.';
    if (!checks.numbers) return 'Add a number.';
    if (!checks.symbols) return 'Add a symbol (e.g. !, @, #, *).';
    return '';
  })();

  return (
    <div
      // The reveal/hide animation is owned by the parent (grid-template-rows
      // + opacity transition on a wrapper). This component just renders the
      // meter; mounting it sits in a height-animating container.
      className="space-y-1.5"
      aria-live="polite"
    >
      <div className="flex items-center gap-2">
        <div className="flex flex-1 gap-1">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={cn('h-1 flex-1 rounded-full transition-colors', barTone(score, i))}
            />
          ))}
        </div>
        {label !== 'empty' ? (
          <span className={cn('text-xs font-medium', LABEL_TONE[label])}>{LABEL_TEXT[label]}</span>
        ) : null}
      </div>
      <p
        className={cn(
          'text-xs',
          meetsRequirements || label === 'empty' ? 'text-muted-foreground' : 'text-destructive',
        )}
      >
        {hint}
      </p>
    </div>
  );
}
